import { useLocation, useHistory } from 'react-router-dom';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { initialState } from 'pages/Users/formConfig';

export const useUsers = (usersList, statuses, activeUsersTypes, meta, fetchUsersPage) => {
  const { search, pathname } = useLocation();
  const { push } = useHistory();

  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const filters = useMemo(
    () => ({
      searchTypeParam: searchParams.get('type'),
      searchNameParam: searchParams.get('name'),
      searchCountryParam: searchParams.get('country'),
      searchClassParam: searchParams.get('class'),
    }),
    [searchParams]
  );

  const [formState, setFormState] = useState(initialState);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [activeTypeIndex, setActiveTypeIndex] = useState(() => {
    const idx = activeUsersTypes.findIndex((userType) => userType === filters.searchTypeParam);
    return idx >= 0 ? idx : 0;
  });

  const debounceTimeout = useRef(null);

  useEffect(() => {
    if (!filters.searchTypeParam) {
      push(`${pathname}?type=${activeUsersTypes[0]}`);
      setActiveTypeIndex(0);
    } else {
      const idx = activeUsersTypes.findIndex((userType) => userType === filters.searchTypeParam);
      if (idx >= 0) setActiveTypeIndex(idx);
      setFormState({
        name: filters.searchNameParam || '',
        country: filters.searchCountryParam || '',
        class: filters.searchClassParam || '',
      });
    }
  }, [filters.searchTypeParam, filters.searchNameParam, filters.searchCountryParam, filters.searchClassParam, pathname, push, activeUsersTypes]);

  useEffect(() => {
    setPage(1);
    if (fetchUsersPage) {
      fetchUsersPage({
        page: 1,
        limit: 20,
        search: filters.searchNameParam || undefined,
        country: filters.searchCountryParam || undefined,
        danceClass: filters.searchClassParam || undefined,
      });
    }
  }, [filters.searchTypeParam, filters.searchNameParam, filters.searchCountryParam, filters.searchClassParam, activeTypeIndex, fetchUsersPage]);

  useEffect(() => {
    const activeUsers = usersList.filter((user) => {
      if (user?.status && statuses && Object.keys(statuses).length > 0) {
        const isActive = statuses[user.status] === 'Active' || user.status === 'Active';
        if (!isActive) return false;
      }
      if (filters.searchCountryParam) {
        const cName = typeof user?.country === 'object' ? user.country?.name : user?.country;
        if (cName && cName.toLowerCase() !== filters.searchCountryParam.toLowerCase()) {
          return false;
        }
      }
      return true;
    });

    setUsers(activeUsers);
  }, [usersList, statuses, filters.searchCountryParam]);

  const loadMore = useCallback(() => {
    setPage((prevPage) => {
      const nextPage = prevPage + 1;
      if (fetchUsersPage) {
        fetchUsersPage({
          page: nextPage,
          limit: 20,
          search: filters.searchNameParam || undefined,
          country: filters.searchCountryParam || undefined,
          danceClass: filters.searchClassParam || undefined,
        });
      }
      return nextPage;
    });
  }, [fetchUsersPage, filters]);

  const hasMore = useMemo(() => {
    if (meta && typeof meta.hasNextPage === 'boolean') {
      return meta.hasNextPage;
    }
    if (meta && typeof meta.totalPages === 'number') {
      return (meta.page || page) < meta.totalPages;
    }
    return Boolean(usersList && usersList.length >= 20);
  }, [meta, page, usersList]);

  const handleFilterChange = (name, value) => {
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = useCallback(
    (e) => {
      if (e && e.preventDefault) e.preventDefault();
      const newParams = new URLSearchParams();
      newParams.set('type', filters.searchTypeParam || activeUsersTypes[0]);

      Object.entries(formState).forEach(([key, value]) => {
        if (value) newParams.set(key, value);
      });

      push(`?${newParams.toString()}`);
    },
    [formState, filters.searchTypeParam, activeUsersTypes, push]
  );

  useEffect(() => {
    if (window.innerWidth < 1440) {
      clearTimeout(debounceTimeout.current);
      debounceTimeout.current = setTimeout(() => {
        handleSubmit({ preventDefault: () => {} });
      }, 300);
    }
  }, [formState, handleSubmit]);

  const clearFilters = () => {
    setFormState(initialState);
    const newParams = new URLSearchParams();
    if (filters.searchTypeParam) {
      newParams.set('type', filters.searchTypeParam);
    }
    push(`?${newParams.toString()}`);
  };

  return {
    users,
    hasMore,
    loadMore,
    formState,
    activeTypeIndex,
    setActiveTypeIndex,
    handleFilterChange,
    handleSubmit,
    clearFilters,
  };
};
